import React, { useRef, useMemo } from "react";

const selectStyle: React.CSSProperties = {
  padding: "0.75rem",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  backgroundColor: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "var(--font-body)",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  marginBottom: "0.5rem",
  fontWeight: "600",
  display: "block",
};

export interface ModalDropdownOption {
  value: string;
  label: string;
  /** Alleen ComboSelectField: klein en cursief achter het label getoond. */
  toelichting?: string;
}

export interface ModalDropdownOptionGroup {
  label: string;
  options: ModalDropdownOption[];
}

export interface ModalDropdownFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Flat options; ignored when `optionGroups` is non-empty. */
  options?: ModalDropdownOption[];
  /** Grouped options (e.g. document categories); preserves group and option order. */
  optionGroups?: ModalDropdownOptionGroup[];
  placeholder?: string;
  sortBy?: "alpha" | "numeric" | "preserve";
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Optional style for the wrapper div */
  wrapperStyle?: React.CSSProperties;
  /** When true and label is empty, hide the label to save space */
  compact?: boolean;
}

export function ModalDropdownField({
  label,
  value,
  onChange,
  options = [],
  optionGroups,
  placeholder,
  sortBy = "alpha",
  required,
  disabled,
  id,
  wrapperStyle,
  compact,
}: ModalDropdownFieldProps) {
  const fieldId = id || `modal-dropdown-${label.replace(/\s/g, "-")}`;
  const selectRef = useRef<HTMLSelectElement>(null);

  const grouped = optionGroups && optionGroups.length > 0;

  const sortedOptions = useMemo(() => {
    const arr = [...options];
    if (sortBy === "preserve") {
      return arr;
    }
    if (sortBy === "alpha") {
      arr.sort((a, b) => a.label.localeCompare(b.label));
    } else if (sortBy === "numeric") {
      arr.sort((a, b) => {
        const na = parseFloat(a.label);
        const nb = parseFloat(b.label);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return a.label.localeCompare(b.label);
      });
    }
    return arr;
  }, [options, sortBy]);

  const flatOptionsForKeyNav = useMemo(() => {
    if (grouped && optionGroups) {
      return optionGroups.flatMap((g) => g.options);
    }
    return sortedOptions;
  }, [grouped, optionGroups, sortedOptions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    // Type-ahead: jump to first option starting with typed character
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const char = e.key.toLowerCase();
      const idx = flatOptionsForKeyNav.findIndex((opt) =>
        opt.label.toLowerCase().startsWith(char),
      );
      if (idx >= 0 && selectRef.current) {
        e.preventDefault();
        selectRef.current.selectedIndex = idx + (placeholder ? 1 : 0);
        onChange(flatOptionsForKeyNav[idx].value);
      }
    }
  };

  const showLabel = !compact || (label && label.length > 0);
  return (
    <div className="modal-field" style={wrapperStyle}>
      {showLabel && (
        <label htmlFor={fieldId} style={labelStyle}>
          {label}
          {required && " *"}
        </label>
      )}
      <select
        ref={selectRef}
        id={fieldId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        onKeyDown={handleKeyDown}
        required={required}
        style={selectStyle}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {grouped && optionGroups
          ? optionGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))
          : sortedOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
      </select>
    </div>
  );
}
